import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Alert, Button, Card, Form, Select, Upload, Spin, Tabs, Modal } from 'antd';
import { FileAddOutlined } from '@ant-design/icons';
import { UploadRequestOption } from 'rc-upload/lib/interface';
import styles from './batch-move.module.scss';
import api from '@services/api';
import { ApiError, Job, StbGroupExtended } from '@services/types';
import { MoveStbGroupBatchModeEnum, MoveStbGroupBatchRequest, StbGroupNameResponse } from '@openapi';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { Navigation } from '@constants/Navigation';
import { downloadFile, selectSearchFilter } from '@services/utils';
import { isEmpty } from 'lodash';

const { Option } = Select;
const { TabPane } = Tabs;

const POLLING_INTERVAL = 5000;

const Batch = () => {
  const [form] = Form.useForm<MoveStbGroupBatchRequest>();
  const [file, setFile] = useState<Blob | undefined>();
  const [stbGroups, setStbGroups] = useState<StbGroupNameResponse[]>([]);
  const [jobId, setJobId] = useState<Job['id']>();
  const [isProcessEnabled, setIsProcessEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [autofillJobId, setAutofillJobId] = useState<Job['id']>();
  const [activeTab, setActiveTab] = useState<string>('1');

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const loadStbGroups = async () => {
      try {
        const data = await api.stbGroups.getStbGroupsFilter();
        setStbGroups(data);
      } catch (error) {
        toast.error('Failed to load STB groups');
        console.error('Failed to load STB groups:', error);
      }
    };

    loadStbGroups();
  }, []);

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const pollJobStatus = useCallback(
    (targetJobId: Job['id']) => {
      stopPolling();

      pollingIntervalRef.current = setInterval(async () => {
        try {
          const jobData = await api.jobs.getJob(targetJobId);

          if (jobData.status !== 'RUNNING') {
            stopPolling();
            setIsLoading(false);

            if (jobData.status === 'FINISHED') {
              setIsProcessEnabled(true);
              toast.success('Job completed successfully');
            } else {
              setIsProcessEnabled(false);
              if (jobData.errorMessage) {
                toast.error(jobData.errorMessage);
              } else {
                toast.error('Job failed');
              }
            }
          }
        } catch (error) {
          console.error('Error polling job status:', error);
          stopPolling();
          setIsLoading(false);
          toast.error('Failed to check job status');
        }
      }, POLLING_INTERVAL);
    },
    [stopPolling],
  );

  const handleFileChange = useCallback((options: UploadRequestOption) => {
    if (typeof options.onSuccess === 'function') {
      options.onSuccess('ok');
    }
    setFile(options.file as Blob);
  }, []);

  const handleSubmit = useCallback(
    async (mode: MoveStbGroupBatchModeEnum) => {
      try {
        setIsLoading(true);
        setIsProcessEnabled(false);

        if (mode !== MoveStbGroupBatchModeEnum.Autofill) {
          await form.validateFields();
        }

        if (!file) {
          throw new Error('File not found');
        }

        const groupId = mode === MoveStbGroupBatchModeEnum.Autofill ? 'null' : form.getFieldValue('id');

        const response = await api.batchMove.exportBatch(groupId, file, mode);

        switch (mode) {
          case MoveStbGroupBatchModeEnum.Process:
            setJobId(response.etc.jobId);
            setIsProcessEnabled(false);
            form.resetFields();
            setFile(undefined);
            toast.success('Export was completed successfully');
            setIsLoading(false);
            break;

          case MoveStbGroupBatchModeEnum.Validate:
            toast.info('Please wait while we validate the file');
            pollJobStatus(response.etc.jobId);
            break;

          case MoveStbGroupBatchModeEnum.Autofill:
            setAutofillJobId(response.etc.jobId);
            toast.info('Please wait while we autofill the file');
            pollJobStatus(response.etc.jobId);
            break;
        }
      } catch (error) {
        console.error('Batch submit error:', error);
        setIsLoading(false);

        if (error instanceof ApiError) {
          toast.error(error?.error.message || 'Operation failed');
        } else if (error instanceof Error) {
          toast.error(error.message);
        } else {
          toast.error('Operation failed');
        }
      }
    },
    [file, form, pollJobStatus],
  );

  const handleDownloadAutofillOutput = useCallback(async () => {
    if (!autofillJobId) return;

    try {
      const response = await api.jobs.getJobReport(autofillJobId);
      if (response) {
        const filename = `${MoveStbGroupBatchModeEnum.Autofill}_(${autofillJobId}).tar.gz`;
        downloadFile(response, filename);
        toast.success('File downloaded successfully');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  }, [autofillJobId]);

  const handleResetJobView = useCallback(() => {
    setJobId(undefined);
    setIsProcessEnabled(false);
  }, []);

  const resetState = useCallback(() => {
    stopPolling();

    setFile(undefined);
    setJobId(undefined);
    setIsProcessEnabled(false);
    setIsLoading(false);
    setAutofillJobId(undefined);

    form.resetFields();
  }, [form, stopPolling]);

  const handleTabChange = useCallback(
    (newTab: string) => {
      const hasUnsavedChanges = file || isLoading || jobId || autofillJobId;

      if (hasUnsavedChanges && newTab !== activeTab) {
        Modal.confirm({
          title: 'Confirm Tab Change',
          content: 'Changing tabs will reset all current progress and uploaded files. Do you want to continue?',
          okText: 'Yes, Continue',
          cancelText: 'No, Stay',
          onOk: () => {
            resetState();
            setActiveTab(newTab);
          },
        });
      } else {
        setActiveTab(newTab);
      }
    },
    [file, isLoading, jobId, autofillJobId, activeTab, resetState],
  );

  const itemRender = useCallback((originNode: React.ReactElement, file: any) => {
    const maxLength = 30;
    const fileName = file.name || '';

    if (fileName.length <= maxLength) {
      return originNode;
    }

    const extension = fileName.substring(fileName.lastIndexOf('.'));
    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
    const truncatedName = nameWithoutExt.substring(0, maxLength - extension.length - 3) + '.. .' + extension;

    return <span>{truncatedName}</span>;
  }, []);

  const renderAutofillTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {!isProcessEnabled ? (
        <Alert
          message="Upload a CSV file containing MAC addresses or serial numbers. After validation, a processed file with valid entries will be available for download and ready for batch move execution."
          type="info"
          showIcon
        />
      ) : (
        <Alert style={{ width: '100%' }} message="Operation successful" type="success" showIcon />
      )}
      <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
        <Upload
          className={styles.upload}
          name="upload"
          accept=".csv"
          customRequest={handleFileChange}
          maxCount={1}
          itemRender={itemRender}
          fileList={file ? [{ uid: '-1', name: (file as File).name || 'file.csv', status: 'done' }] : []}
        >
          <Button type="default" icon={<FileAddOutlined />}>
            Choose File
          </Button>
        </Upload>
        <Button
          disabled={isEmpty(file) || isLoading}
          onClick={() => handleSubmit(MoveStbGroupBatchModeEnum.Autofill)}
          type="primary"
        >
          Autofill
        </Button>
        <Button disabled={!isProcessEnabled || isLoading} onClick={handleDownloadAutofillOutput} type="primary">
          Download File
        </Button>
        {isLoading && <Spin />}
      </div>
    </div>
  );

  const renderBatchMoveTab = () => {
    if (jobId) {
      return (
        <div style={{ display: 'grid', gridGap: '10px', justifyItems: 'center' }}>
          <Alert style={{ width: '100%' }} message="Operation successful" type="success" showIcon />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'max-content max-content',
              gridGap: '10px',
            }}
          >
            <Button style={{ width: '250px' }} type="default" onClick={handleResetJobView}>
              Go to Batch Move
            </Button>
            <Button style={{ width: '250px' }} type="primary">
              <Link to={Navigation.JobInfo.replace(':id', jobId)}>Go to Job</Link>
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div>
        <Alert message="You can proceed only after checking and analyzing the results." type="info" showIcon />
        <Form className={styles.form} form={form}>
          <Form.Item
            className={styles.stbGroup}
            name="id"
            label="Destination group"
            rules={[
              {
                required: true,
                message: 'Please choose the destination group',
              },
            ]}
          >
            <Select allowClear showSearch filterOption={selectSearchFilter}>
              {stbGroups.map((group) => (
                <Option key={group.id} value={group.id}>
                  {`${group.name}(${group.deviceTypeName}, ${group.scope})`}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            className={styles.upload}
            rules={[
              {
                required: true,
                message: 'Please choose the file',
              },
            ]}
            name="file"
          >
            <Upload
              name="upload"
              accept=".csv"
              customRequest={handleFileChange}
              maxCount={1}
              fileList={file ? [{ uid: '-1', name: (file as File).name || 'file.csv', status: 'done' }] : []}
            >
              <Button type="default" icon={<FileAddOutlined />}>
                Choose File
              </Button>
            </Upload>
          </Form.Item>
          <div className={styles.submit}>
            <Button
              className={styles.validate}
              disabled={isLoading}
              onClick={() => {
                setIsProcessEnabled(false);
                handleSubmit(MoveStbGroupBatchModeEnum.Validate);
              }}
              type="primary"
            >
              Test Input File
            </Button>
            {isLoading && <Spin />}
            <Button
              disabled={!isProcessEnabled}
              className={styles.process}
              onClick={() => handleSubmit(MoveStbGroupBatchModeEnum.Process)}
              type="primary"
            >
              Process
            </Button>
          </div>
        </Form>
      </div>
    );
  };

  return (
    <Card title="Batch Move and Provisioning">
      <Tabs activeKey={activeTab} type="card" size="large" onChange={handleTabChange}>
        <TabPane tab="Batch File Processing" key="2">
          {renderAutofillTab()}
        </TabPane>
        <TabPane tab="Batch Move" key="1">
          {renderBatchMoveTab()}
        </TabPane>
      </Tabs>
    </Card>
  );
};

export default React.memo(Batch);
